import type { SceneFactProposalAction } from "@sitescene/types";

/**
 * API endpoint paths. Kept in one place so route strings are not scattered
 * across hooks and components.
 */
export const ENDPOINTS = {
	shadow: {
		costs: "/shadow/costs",
		events: {
			byMeasurement: (measurementId: string) =>
				`/shadow/events/by-measurement/${measurementId}`,
			event: (eventId: string) => `/shadow/events/${eventId}`,
			reanalysis: (eventId: string) => `/shadow/events/${eventId}/reanalysis`,
			reanalysisAction: (
				eventId: string,
				reanalysisId: string,
				action: "check" | "force" | "revert",
			) => `/shadow/events/${eventId}/reanalysis/${reanalysisId}/${action}`,
			reanalysisSuggest: (eventId: string) =>
				`/shadow/events/${eventId}/reanalysis/suggest`,
			video: (eventId: string) => `/shadow/events/${eventId}/video`,
		},
		floors: {
			create: "/shadow/floors",
			update: (floorId: string) => `/shadow/floors/${floorId}`,
		},
		installations: {
			create: "/shadow/installations",
			list: "/shadow/installations",
		},
		locations: {
			create: "/shadow/locations",
			list: "/shadow/locations",
			update: (locationId: string) => `/shadow/locations/${locationId}`,
		},
		me: "/shadow/me",
		organizations: "/shadow/organizations",
		plausibility: {
			backfill: "/shadow/plausibility/backfill",
			calibrationProposals: "/shadow/plausibility/calibration/proposals",
			calibrationReview: (proposalId: string, action: "accept" | "reject") =>
				`/shadow/plausibility/calibration/proposals/${proposalId}/${action}`,
			calibrationRun: "/shadow/plausibility/calibration/run",
			globalPrior: (priorId: string) =>
				`/shadow/plausibility/global/priors/${priorId}`,
			globalPriors: "/shadow/plausibility/global/priors",
			prior: (priorId: string) => `/shadow/plausibility/priors/${priorId}`,
			priors: "/shadow/plausibility/priors",
			replay: "/shadow/plausibility/replay",
			reverify: "/shadow/plausibility/reverify",
			seed: "/shadow/plausibility/seed",
			sensorSpec: (sensorId: string, classId: string) =>
				`/shadow/plausibility/sensor-specs/draft/${encodeURIComponent(sensorId)}/${encodeURIComponent(classId)}`,
			sensorSpecsDraft: "/shadow/plausibility/sensor-specs/draft",
			sensorSpecsPublish: "/shadow/plausibility/sensor-specs/draft/publish",
			sensorSpecsWorkspace: "/shadow/plausibility/sensor-specs/workspace",
			taxonomy: "/shadow/plausibility/taxonomy",
			taxonomyClass: (classId: string) =>
				`/shadow/plausibility/taxonomy/draft/classes/${encodeURIComponent(classId)}`,
			taxonomyDraft: "/shadow/plausibility/taxonomy/draft",
			taxonomyExport: "/shadow/plausibility/taxonomy/export",
			taxonomyPublish: "/shadow/plausibility/taxonomy/draft/publish",
			taxonomySpec: (classId: string) =>
				`/shadow/plausibility/taxonomy/draft/specs/${encodeURIComponent(classId)}`,
			taxonomyWorkspace: "/shadow/plausibility/taxonomy/workspace",
			unclassified: "/shadow/plausibility/unclassified",
			unclassifiedAlert: (id: string) =>
				`/shadow/plausibility/unclassified/${encodeURIComponent(id)}`,
			unclassifiedIgnore: "/shadow/plausibility/unclassified/ignore",
			unclassifiedResolve: "/shadow/plausibility/unclassified/resolve",
			unclassifiedSuggest: (id: string) =>
				`/shadow/plausibility/unclassified/${encodeURIComponent(id)}/suggest`,
			verdict: (verificationId: string) =>
				`/shadow/plausibility/verifications/${verificationId}/verdict`,
			verdictSuggest: (verificationId: string) =>
				`/shadow/plausibility/verifications/${verificationId}/verdict/suggest`,
			verification: (verificationId: string) =>
				`/shadow/plausibility/verifications/${verificationId}`,
			verifications: "/shadow/plausibility/verifications",
			verificationVideo: (verificationId: string) =>
				`/shadow/plausibility/verifications/${verificationId}/video`,
		},
		reanalyses: "/shadow/reanalysis",
		sceneFacts: {
			proposal: (proposalId: string) =>
				`/shadow/scene-facts/proposals/${proposalId}`,
			proposalAction: (proposalId: string, action: SceneFactProposalAction) =>
				`/shadow/scene-facts/proposals/${proposalId}/${action}`,
			proposalRetest: (proposalId: string) =>
				`/shadow/scene-facts/proposals/${proposalId}/retest`,
			proposals: "/shadow/scene-facts/proposals",
			proposalVideo: (proposalId: string) =>
				`/shadow/scene-facts/proposals/${proposalId}/video`,
		},
		sensors: {
			create: "/shadow/sensors",
			update: (sensorId: string) => `/shadow/sensors/${sensorId}`,
		},
		tenantSettings: "/shadow/tenant/settings",
		zones: {
			create: "/shadow/zones",
			update: (zoneId: string) => `/shadow/zones/${zoneId}`,
		},
	},
} as const;
